/*
 *
 * Copyright (c) 2025 Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Alexander Orlov ("CONFIDENTIAL INFORMATION").
 *
 * Author: Alexander Orlov
 * Aegis AO Soft
 *
 */

import apiClient from './client';
import {
  OwnerTollBalance,
  UpdateOwnerTollSettings,
  TollPaymentLogEntry,
  OwnerTollPayout,
  PartnerInfo,
  StripeAccountStatus,
} from '@/shared/components/owners/types';

export interface TollPaymentEntry {
  id: string;
  tollDate: string | null;
  totalTollAmount: number;
  totalCommision: number;
  total: number;
  platform: string | null;
  tollSystemName: string | null;
  invoiceStatus: number;
  paymentStatus: number;
  details: string | null;
  numberOfTolls: number | null;
  actualTollAmount: number | null;
  dateCreated: string;
}

export interface TollInvoiceEntry {
  id: number;
  tollDate: string;
  tollTime: string;
  tollAuthority: string;
  tollAuthorityDescription: string;
  entry: string;
  exit: string;
  amount: number;
  adjustedAmount: number | null;
  plateNumber: string;
  transponderNumber: string;
  transactionType: string;
  dailyPaymentId: string;
  // GPS reconciliation, materialised by the backend (GpsFirstTollService) onto the invoice row itself:
  //  • source     — "Agency" | "Gps" | "Both" (which side(s) landed for this toll);
  //  • gpsAmount  — the raw GPS toll value paired to this agency toll (may lag a reprice);
  //  • gpsTollId  — the dbo.GpsTolls row it is linked to (used to re-resolve the fresh amount client-side).
  source?: string | null;
  gpsAmount?: number | null;
  gpsTollId?: number | null;
  /**
   * IANA zone of the agency that scraped this toll (TollTimeZone.ResolveId on the backend). tollDate +
   * tollTime are UTC, and the crossing happened on the road's clock, not the reader's — rendering the two
   * on different clocks is what made the modal say 4:45 PM where the invoice said 20:45.
   */
  agencyTimeZone?: string | null;
  /**
   * "EDT" / "PST". Only set on rows built for a DOCUMENT (billedRowsToTollDetails), where tollDate and
   * tollTime hold the agency's wall clock rather than UTC and the label has to be printed beside them.
   * Absent on rows straight from the API, whose tollDate/tollTime are UTC.
   */
  timeZoneAbbr?: string | null;
}

export interface BookingTollsResponse {
  charges: TollPaymentLogEntry[];
  payments: TollPaymentEntry[];
  tolls: TollInvoiceEntry[];
  // Owner charging policy — lets the modal highlight which amount is actually billed to the driver.
  distributionMode?: string | null;
  chargeMissingGpsTolls?: boolean;
  tollServiceFee?: number;
  /** 'PerToll' (a fee on every toll) | 'PerBooking' (a single fee for the whole booking). */
  tollFeeMode?: string;
}

// Owner row shown on the partner's "Partners" page — owner whose balance.PartnerId = current user id
export interface PartnershipOwner {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  distributionMode: string;
  ownerSharePercent: number;
  partnerSharePercent: number;
  payoutPeriodDays: number;
  payoutThresholdAmount: number;
  accumulatedPartnerShare: number;
  // Partner earning term for this owner: null duration / end date = unlimited; active=false once it expired.
  partnerShareDurationDays: number | null;
  partnerShareEndsAt: string | null;
  partnerShareActive: boolean;
}

const BASE = '/OwnerTollBalance';

// Helper: extract payload from API response.
// Handles both wrapped { result: T } (standard APIResponse) and raw T formats.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const unwrap = <T>(data: any): T => data?.result ?? data;

export const ownerTollBalanceApi = {
  // ─── Admin endpoints (require Admin role) ─────────────────────────

  // Get balance + settings for a specific owner
  getBalance: async (ownerId: string): Promise<OwnerTollBalance> => {
    const response = await apiClient.get(`${BASE}/admin/balance/${ownerId}`);
    return unwrap(response.data);
  },

  // Update settings for a specific owner
  saveSettings: async (ownerId: string, data: UpdateOwnerTollSettings): Promise<OwnerTollBalance> => {
    const response = await apiClient.put(`${BASE}/admin/settings/${ownerId}`, data);
    return unwrap(response.data);
  },

  // Payout history for a specific owner
  getPayouts: async (ownerId: string, from?: string, to?: string): Promise<OwnerTollPayout[]> => {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const qs = params.toString();
    const response = await apiClient.get(`${BASE}/admin/payouts/${ownerId}${qs ? `?${qs}` : ''}`);
    return unwrap(response.data) ?? [];
  },

  // Charges log for a specific owner
  getCharges: async (ownerId: string, from?: string, to?: string): Promise<TollPaymentLogEntry[]> => {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const qs = params.toString();
    const response = await apiClient.get(`${BASE}/admin/charges/${ownerId}${qs ? `?${qs}` : ''}`);
    return unwrap(response.data) ?? [];
  },

  // Manual payout for a specific owner
  triggerPayout: async (ownerId: string): Promise<OwnerTollPayout> => {
    const response = await apiClient.post(`${BASE}/admin/payout/${ownerId}`);
    return unwrap(response.data);
  },

  // Stripe onboarding – create connected account & return onboarding URL
  startStripeOnboarding: async (
    ownerId: string,
    returnUrl: string,
    refreshUrl: string,
    businessType?: 'individual' | 'company',
  ): Promise<{ accountId: string; onboardingUrl: string }> => {
    const response = await apiClient.post(`${BASE}/admin/stripe-onboard/${ownerId}`, {
      returnUrl,
      refreshUrl,
      businessType,
    });
    return unwrap(response.data);
  },

  // Get all partners
  getPartners: async (): Promise<PartnerInfo[]> => {
    const response = await apiClient.get(`${BASE}/admin/partners`);
    return unwrap(response.data);
  },

  // Owners whose balance.PartnerId == partner user id (current user by default;
  // admins may pass ownerId to view another user's partnership list)
  getMyPartnershipOwners: async (ownerId?: string): Promise<PartnershipOwner[]> => {
    const response = await apiClient.get(`${BASE}/my-partnership-owners`, {
      params: ownerId ? { ownerId } : undefined,
    });
    return unwrap(response.data) || [];
  },

  // Server-paged partner owner list (page/pageSize + name/email/id search).
  getMyPartnershipOwnersPaged: async (
    opts?: { ownerId?: string; page?: number; pageSize?: number; search?: string }
  ): Promise<{ items: PartnershipOwner[]; totalCount: number; page: number; pageSize: number }> => {
    const pageSize = opts?.pageSize ?? 25;
    const response = await apiClient.get(`${BASE}/my-partnership-owners-paged`, {
      params: {
        ownerId: opts?.ownerId,
        page: opts?.page ?? 1,
        pageSize,
        search: opts?.search?.trim() || undefined,
      },
    });
    return unwrap(response.data) || { items: [], totalCount: 0, page: opts?.page ?? 1, pageSize };
  },

  // Stripe account status
  getStripeStatus: async (ownerId: string): Promise<StripeAccountStatus> => {
    const response = await apiClient.get(`${BASE}/admin/stripe-status/${ownerId}`);
    return unwrap(response.data);
  },

  // Disconnect Stripe connected account (admin)
  disconnectStripe: async (
    ownerId: string,
  ): Promise<{ message: string; disconnectedAccountId: string; pendingBalance: number }> => {
    const response = await apiClient.post(`${BASE}/admin/stripe-disconnect/${ownerId}`);
    return unwrap(response.data);
  },

  // Reconnect Stripe account (admin – creates new account + onboarding)
  reconnectStripe: async (
    ownerId: string,
    returnUrl: string,
    refreshUrl: string,
    businessType?: 'individual' | 'company',
  ): Promise<{ accountId: string; onboardingUrl: string }> => {
    const response = await apiClient.post(`${BASE}/admin/stripe-reconnect/${ownerId}`, {
      returnUrl,
      refreshUrl,
      businessType,
    });
    return unwrap(response.data);
  },

  // ─── Partner read-only endpoints (client owner linked to this partner) ─

  // Balance + settings for a client owner (backend verifies balance.PartnerId == current user)
  getPartnerBalance: async (ownerId: string): Promise<OwnerTollBalance> => {
    const response = await apiClient.get(`${BASE}/partner/balance/${ownerId}`);
    return unwrap(response.data);
  },

  // Update the non-restricted settings of a client owner (backend whitelists editable fields)
  savePartnerSettings: async (ownerId: string, data: UpdateOwnerTollSettings): Promise<OwnerTollBalance> => {
    const response = await apiClient.put(`${BASE}/partner/settings/${ownerId}`, data);
    return unwrap(response.data);
  },

  // Payout history for a client owner (partner read-only)
  getPartnerPayouts: async (ownerId: string, from?: string, to?: string): Promise<OwnerTollPayout[]> => {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const qs = params.toString();
    const response = await apiClient.get(`${BASE}/partner/payouts/${ownerId}${qs ? `?${qs}` : ''}`);
    return unwrap(response.data) ?? [];
  },

  // Charges log for a client owner (partner read-only)
  getPartnerCharges: async (ownerId: string, from?: string, to?: string): Promise<TollPaymentLogEntry[]> => {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const qs = params.toString();
    const response = await apiClient.get(`${BASE}/partner/charges/${ownerId}${qs ? `?${qs}` : ''}`);
    return unwrap(response.data) ?? [];
  },

  // Stripe account status for a client owner (partner read-only)
  getPartnerStripeStatus: async (ownerId: string): Promise<StripeAccountStatus> => {
    const response = await apiClient.get(`${BASE}/partner/stripe-status/${ownerId}`);
    return unwrap(response.data);
  },

  // Get charges + invoices for a specific booking (self-service, verifies ownership on backend)
  getChargesByBooking: async (bookingId: string): Promise<BookingTollsResponse> => {
    const response = await apiClient.get(`${BASE}/charges-by-booking/${bookingId}`);
    const data = unwrap<BookingTollsResponse>(response.data);
    // Preserve the charging-policy fields (distributionMode/chargeMissingGpsTolls/tollServiceFee) — the
    // modal needs them to highlight the billed amount and to size GPS totals; a hardcoded 3-field object
    // used to drop them silently.
    return { ...data, charges: data?.charges ?? [], payments: data?.payments ?? [], tolls: data?.tolls ?? [] };
  },

  // Get charges + invoices for a specific booking (admin — any owner's booking)
  getChargesByBookingAdmin: async (bookingId: string): Promise<BookingTollsResponse> => {
    const response = await apiClient.get(`${BASE}/admin/charges-by-booking/${bookingId}`);
    const data = unwrap<BookingTollsResponse>(response.data);
    return { ...data, charges: data?.charges ?? [], payments: data?.payments ?? [], tolls: data?.tolls ?? [] };
  },

  // ─── Self-service endpoints (owner views own data via JWT) ────────

  // Get own balance + settings (no ownerId needed, resolved from JWT)
  getSelfBalance: async (): Promise<OwnerTollBalance> => {
    const response = await apiClient.get(`${BASE}/balance`);
    return unwrap(response.data);
  },

  // Update own settings
  saveSelfSettings: async (data: UpdateOwnerTollSettings): Promise<OwnerTollBalance> => {
    const response = await apiClient.put(`${BASE}/settings`, data);
    return unwrap(response.data);
  },

  // Own payout history
  getSelfPayouts: async (from?: string, to?: string): Promise<OwnerTollPayout[]> => {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const qs = params.toString();
    const response = await apiClient.get(`${BASE}/payouts${qs ? `?${qs}` : ''}`);
    return unwrap(response.data) ?? [];
  },

  // Own charges log
  getSelfCharges: async (from?: string, to?: string): Promise<TollPaymentLogEntry[]> => {
    const params = new URLSearchParams();
    if (from) params.append('from', from);
    if (to) params.append('to', to);
    const qs = params.toString();
    const response = await apiClient.get(`${BASE}/charges${qs ? `?${qs}` : ''}`);
    return unwrap(response.data) ?? [];
  },

  // Own Stripe onboarding (self-service)
  startSelfStripeOnboarding: async (
    returnUrl: string,
    refreshUrl: string,
    businessType?: 'individual' | 'company',
  ): Promise<{ accountId: string; onboardingUrl: string }> => {
    const response = await apiClient.post(`${BASE}/stripe-onboard`, {
      returnUrl,
      refreshUrl,
      businessType,
    });
    return unwrap(response.data);
  },

  // Own Stripe status (self-service)
  getSelfStripeStatus: async (): Promise<StripeAccountStatus> => {
    const response = await apiClient.get(`${BASE}/stripe-status`);
    return unwrap(response.data);
  },

  // Disconnect own Stripe connected account (self-service)
  disconnectSelfStripe: async (): Promise<{ message: string; disconnectedAccountId: string; pendingBalance: number }> => {
    const response = await apiClient.post(`${BASE}/stripe-disconnect`);
    return unwrap(response.data);
  },

  // Reconnect own Stripe account (self-service – creates new account + onboarding)
  reconnectSelfStripe: async (
    returnUrl: string,
    refreshUrl: string,
    businessType?: 'individual' | 'company',
  ): Promise<{ accountId: string; onboardingUrl: string }> => {
    const response = await apiClient.post(`${BASE}/stripe-reconnect`, {
      returnUrl,
      refreshUrl,
      businessType,
    });
    return unwrap(response.data);
  },

  // Revalidate own Stripe account (self-service — auto-recovers from PaymentsInfo if NULL)
  revalidateSelfStripe: async (): Promise<{ status: string; accountId?: string; message: string }> => {
    const response = await apiClient.post(`${BASE}/stripe-revalidate`);
    return unwrap(response.data);
  },
};

export const ownerTollBalanceQueryKeys = {
  all: ['owner-toll-balance'] as const,
  balance: (ownerId: string) => [...ownerTollBalanceQueryKeys.all, 'balance', ownerId] as const,
  payouts: (ownerId: string) => [...ownerTollBalanceQueryKeys.all, 'payouts', ownerId] as const,
  charges: (ownerId: string) => [...ownerTollBalanceQueryKeys.all, 'charges', ownerId] as const,
  stripeStatus: (ownerId: string) => [...ownerTollBalanceQueryKeys.all, 'stripe-status', ownerId] as const,
  partners: [...['owner-toll-balance'], 'partners'] as const,
  // Self-service keys (owner viewing own data)
  selfBalance: [...['owner-toll-balance'], 'self', 'balance'] as const,
  selfPayouts: [...['owner-toll-balance'], 'self', 'payouts'] as const,
  selfCharges: [...['owner-toll-balance'], 'self', 'charges'] as const,
  selfStripeStatus: [...['owner-toll-balance'], 'self', 'stripe-status'] as const,
};
